import { ThemeEvents, CartErrorEvent, CartUpdateEvent } from '@theme/events';
import { applyCartResponse, getCartDrawerSectionId } from '@theme/cart-drawer';

class ProductBuyButtons extends HTMLElement {
  #controller = null;
  #form = null;
  #submitButton = null;
  #isAvailable = true;
  #handleSubmitBound = null;
  #onVariantUpdateBound = null;
  #onPurchaseTypeUpdateBound = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    if (!this.#form) return;

    this.#submitButton = this.#form.querySelector('[data-add-to-cart]');
    if (!(this.#submitButton instanceof HTMLButtonElement)) return;

    this.#isAvailable = !this.#submitButton.disabled;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#handleSubmitBound = this.#handleSubmit.bind(this);
    this.#onVariantUpdateBound = this.#onVariantUpdate.bind(this);
    this.#onPurchaseTypeUpdateBound = this.#onPurchaseTypeUpdate.bind(this);

    this.#form.addEventListener('submit', this.#handleSubmitBound, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateBound, { signal });
    document.addEventListener(ThemeEvents.purchaseTypeUpdate, this.#onPurchaseTypeUpdateBound, { signal });

    this.#syncActiveVariantFromSelector();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #onVariantUpdate(event) {
    const productId = this.dataset.productId;
    const detail = (event).detail;

    if (productId && detail?.data?.productId && String(detail.data.productId) !== String(productId)) {
      return;
    }

    this.#syncActiveVariantFromSelector(detail?.resource);
  }

  #onPurchaseTypeUpdate(event) {
    const productId = this.dataset.productId;
    const detail = event.detail;

    if (productId && detail?.productId && String(detail.productId) !== String(productId)) {
      return;
    }

    this.#syncSellingPlanInput(detail?.sellingPlanId);
    this.#syncActiveVariantFromSelector();
  }

  #getSupplySelector() {
    const productId = this.dataset.productId;
    const productInfo = this.closest('[data-product-info]');

    return productInfo?.querySelector(
      `product-supply-selector[data-product-id="${productId}"]`,
    );
  }

  #syncActiveVariantFromSelector(resource) {
    const selector = this.#getSupplySelector();
    const active = typeof selector?.getActiveVariant === 'function'
      ? selector.getActiveVariant()
      : null;
    const variantId = active?.variantId || resource?.id || '';
    const available = active
      ? Boolean(active.available && active.variantId)
      : resource?.available !== false && Boolean(variantId);

    const variantInput = this.#form?.querySelector('[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId ? String(variantId) : '';
    }

    this.#isAvailable = available;
    this.#refreshButtonAvailability();
  }

  #getPurchaseToggle() {
    const productId = this.dataset.productId;
    const productInfo = this.closest('[data-product-info]');

    return productInfo?.querySelector(
      `product-purchase-toggle[data-product-id="${productId}"]`,
    );
  }

  #syncSellingPlanInput(sellingPlanId) {
    const input = this.#form?.querySelector('[name="selling_plan"]');
    if (!(input instanceof HTMLInputElement)) return;

    const planId = sellingPlanId ? String(sellingPlanId) : '';

    if (planId) {
      input.disabled = false;
      input.value = planId;
      return;
    }

    input.value = '';
    input.disabled = true;
  }

  #resolveSellingPlanId() {
    const purchaseToggle = this.#getPurchaseToggle();
    const isOneTimePurchase = purchaseToggle?.querySelector('[data-purchase-type="one_time"]:checked');

    if (isOneTimePurchase) return null;

    const formInput = this.#form?.querySelector('[name="selling_plan"]');
    const formPlanId = formInput instanceof HTMLInputElement ? formInput.value : '';
    const togglePlanInput = purchaseToggle?.querySelector('[data-plan-select]');
    const togglePlanId = togglePlanInput instanceof HTMLInputElement ? togglePlanInput.value : '';
    const planId = Number(formPlanId || togglePlanId);

    return planId > 0 ? planId : null;
  }

  #refreshButtonAvailability() {
    const productId = this.dataset.productId;
    const productInfo = this.closest('[data-product-info]');
    const selectedInput = productInfo?.querySelector(
      `product-supply-selector[data-product-id="${productId}"] [data-supply-group]:not([hidden]) .product-supply-selector__input:checked`,
    );
    const cardAvailable = selectedInput instanceof HTMLInputElement && !selectedInput.disabled;

    this.#setButtonState({ loading: false, available: this.#isAvailable && cardAvailable });
  }

  #getFormData() {
    const formData = this.#form ? new FormData(this.#form) : new FormData();
    const sellingPlanId = this.#resolveSellingPlanId();

    if (sellingPlanId) {
      formData.set('selling_plan', String(sellingPlanId));
    } else {
      formData.delete('selling_plan');
    }

    return formData;
  }

  #getChildVariantIds() {
    const productInfo = this.closest('[data-product-info]');
    const purchaseToggle = this.#getPurchaseToggle();
    const isOneTimePurchase = purchaseToggle?.querySelector('[data-purchase-type="one_time"]:checked');

    if (isOneTimePurchase) return [];

    const bundleCard = productInfo?.querySelector('[data-nested-items]');
    const bundleProductElements = bundleCard?.querySelectorAll('[data-variant-id][data-auto-add-to-cart="true"]');
    return bundleProductElements?.length > 0
      ? Array.from(bundleProductElements)
          .map((el) => Number(el.dataset.variantId))
          .filter((id) => Boolean(id))
      : [];
  }

  async #handleSubmit(event) {
    event.preventDefault();

    if (!this.#submitButton || this.#submitButton.disabled || !this.#isAvailable) return;

    this.#setButtonState({ loading: true, available: this.#isAvailable });

    try {
      const formData = this.#getFormData();
      const parentVariantId = Number(formData.get('id'));
      const quantity = Number(formData.get('quantity')) || 1;
      const childVariants = this.#getChildVariantIds();

      if (!parentVariantId) return;

      const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const sectionId = getCartDrawerSectionId();
      const sectionsPayload = sectionId ? { sections: sectionId } : {};

      let response;

      if (childVariants.length > 0) {
        const sellingPlanId = this.#resolveSellingPlanId();
        const parentItem = sellingPlanId
          ? { id: parentVariantId, quantity, selling_plan: sellingPlanId }
          : { id: parentVariantId, quantity };

        const items = [
          parentItem,
          ...childVariants.map((id) => ({
            id,
            quantity: 1,
            parent_id: parentVariantId,
          })),
        ];

        response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items, ...sectionsPayload }),
        });
      } else if (sectionId) {
        formData.append('sections', sectionId);
        response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
      } else {
        response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
      }

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.description || errorBody.message || 'Add to cart failed');
      }

      const result = await response.json();
      const cart = await applyCartResponse(result);

      document.dispatchEvent(
        new CartUpdateEvent(cart || result, this.dataset.blockId || '', {
          source: 'product-buy-buttons',
          variantId: String(parentVariantId),
        }),
      );
    } catch (error) {
      console.error('[product-buy-buttons] Failed to add product to cart', error);
      document.dispatchEvent(new CartErrorEvent(this.dataset.blockId || '', error?.message || 'Add to cart failed', null, null));
    } finally {
      this.#refreshButtonAvailability();
    }
  }

  #setButtonState({ loading, available }) {
    const button = this.#submitButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const textElement = button.querySelector('.add-to-cart__label');
    const defaultLabel = button.dataset.labelDefault || '';
    const loadingLabel = button.dataset.labelLoading || defaultLabel;
    const unavailableLabel = button.dataset.labelUnavailable || defaultLabel;

    if (loading) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-label', loadingLabel);

      if (textElement instanceof HTMLElement) {
        textElement.textContent = loadingLabel;
      }

      return;
    }

    button.removeAttribute('aria-busy');
    button.disabled = !available;
    button.setAttribute('aria-disabled', available ? 'false' : 'true');
    button.setAttribute('aria-label', available ? defaultLabel : unavailableLabel);

    if (textElement instanceof HTMLElement) {
      textElement.textContent = available ? defaultLabel : unavailableLabel;
    }
  }
}

if (!customElements.get('product-buy-buttons')) {
  customElements.define('product-buy-buttons', ProductBuyButtons);
}
